import { useCallback, useEffect, useRef, useState } from "react";
import { invokeCallHub } from "../realtime/callHubClient.js";
import { CALL_HUB_EVENTS, CALL_HUB_METHODS } from "../realtime/callEvents.js";
import { getGlobalCallRingtone } from "../realtime/callRingtone.js";
import {
  connectToLiveKitRoom,
  disconnectLiveKitRoom,
  holdLiveKitCall,
  transferLiveKitCall,
} from "@/utils/livekitWebEngine.js";

import { CALL_STATES } from "../callConstants.js";
export { CALL_STATES };

const debugCall = (...args) => {
  if (import.meta.env?.DEV) {
    console.debug("[call-session]", ...args);
  }
};

export const useCallSession = ({ connection, remoteAudioRef, onCallEnded }) => {
  const liveKitRoomRef = useRef(null);
  const heldLiveKitRoomRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callStateRef = useRef(CALL_STATES.idle);
  const callInfoRef = useRef(null);
  const onCallEndedRef = useRef(onCallEnded);
  const [callState, setCallState] = useState(CALL_STATES.idle);
  const [callInfo, setCallInfoState] = useState(null);
  const [waitingCall, setWaitingCall] = useState(null);
  const [heldCall, setHeldCall] = useState(null);
  const [muted, setMuted] = useState(false);
  const [isHeld, setIsHeld] = useState(false);
  const [isRemoteHeld, setIsRemoteHeld] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferringDepartment, setTransferringDepartment] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [lastError, setLastError] = useState(null);
  const [lastEndReason, setLastEndReason] = useState(null);

  const setCallInfo = useCallback((nextInfo) => {
    setCallInfoState((current) => {
      const resolved = typeof nextInfo === "function" ? nextInfo(current) : nextInfo;
      callInfoRef.current = resolved;
      return resolved;
    });
  }, []);

  const connectingTimerRef = useRef(null);
  const durationTimerRef = useRef(null);

  const clearConnectingTimer = useCallback(() => {
    if (connectingTimerRef.current) {
      clearTimeout(connectingTimerRef.current);
      connectingTimerRef.current = null;
    }
  }, []);

  const clearDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  const resetCall = useCallback(() => {
    clearConnectingTimer();
    clearDurationTimer();
    ringtoneRef.current?.stop();
    if (liveKitRoomRef.current) {
      disconnectLiveKitRoom(liveKitRoomRef.current);
      liveKitRoomRef.current = null;
    }
    if (heldLiveKitRoomRef.current) {
      disconnectLiveKitRoom(heldLiveKitRoomRef.current);
      heldLiveKitRoomRef.current = null;
    }
    setMuted(false);
    setIsHeld(false);
    setIsRemoteHeld(false);
    setIsTransferring(false);
    setTransferringDepartment(null);
    setCallDuration(0);
    setWaitingCall(null);
    setHeldCall(null);
  }, [clearConnectingTimer, clearDurationTimer]);

  useEffect(() => {
    onCallEndedRef.current = onCallEnded;
  }, [onCallEnded]);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  useEffect(() => {
    callInfoRef.current = callInfo;
  }, [callInfo]);

  // Duration stopwatch when connected
  useEffect(() => {
    if (callState === CALL_STATES.connected) {
      setCallDuration(0);
      durationTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      clearDurationTimer();
      setCallDuration(0);
    }

    return () => clearDurationTimer();
  }, [callState, clearDurationTimer]);

  // Handle SignalR CallWaiting events when connected
  useEffect(() => {
    if (!connection) return;

    const handleCallWaitingEvent = (payload) => {
      if (callStateRef.current === CALL_STATES.connected) {
        setWaitingCall(payload);
      }
    };

    connection.on?.(CALL_HUB_EVENTS.callWaiting, handleCallWaitingEvent);

    return () => {
      connection.off?.(CALL_HUB_EVENTS.callWaiting, handleCallWaitingEvent);
    };
  }, [connection]);

  useEffect(() => {
    ringtoneRef.current = getGlobalCallRingtone();

    if (callState === CALL_STATES.incoming) {
      ringtoneRef.current.start("incoming");
    } else if (callState === CALL_STATES.calling) {
      ringtoneRef.current.start("outgoing");
    } else {
      ringtoneRef.current.stop();
    }
  }, [callState]);

  useEffect(() => {
    if (callState === CALL_STATES.connecting) {
      clearConnectingTimer();
      connectingTimerRef.current = setTimeout(() => {
        if (callStateRef.current === CALL_STATES.connecting) {
          debugCall("connecting timeout reached");
          resetCall();
          setCallState(CALL_STATES.idle);
          setCallInfo(null);
          onCallEndedRef.current?.();
        }
      }, 15000);
    } else {
      clearConnectingTimer();
    }

    return () => clearConnectingTimer();
  }, [callState, clearConnectingTimer, resetCall, setCallInfo]);

  useEffect(() => {
    ringtoneRef.current = getGlobalCallRingtone();

    return () => {
      clearConnectingTimer();
      clearDurationTimer();
      ringtoneRef.current?.stop();
    };
  }, [clearConnectingTimer, clearDurationTimer]);

  const failCall = useCallback((error, message = "Call error") => {
    debugCall("failCall", message, error);
    const errObj = typeof error === "string" ? new Error(error) : error || new Error(message);
    setLastError(errObj);
    resetCall();
    setCallState(CALL_STATES.idle);
    setCallInfo(null);
    onCallEndedRef.current?.();
  }, [resetCall, setCallInfo]);

  const connectLiveKit = useCallback(async (callId) => {
    if (!callId) return;
    try {
      if (liveKitRoomRef.current && liveKitRoomRef.current.state === "connected") {
        clearConnectingTimer();
        if (callStateRef.current !== CALL_STATES.calling) {
          setCallState(CALL_STATES.connected);
        }
        setIsTransferring(false);
        setIsRemoteHeld(false);
        try {
          await liveKitRoomRef.current.startAudio();
          if (liveKitRoomRef.current.localParticipant) {
            await liveKitRoomRef.current.localParticipant.setMicrophoneEnabled(true);
          }
          if (liveKitRoomRef.current.remoteParticipants) {
            liveKitRoomRef.current.remoteParticipants.forEach((participant) => {
              if (participant?.trackPublications) {
                participant.trackPublications.forEach((pub) => {
                  if (pub?.kind === 'audio' || pub?.track?.kind === 'audio') {
                    try {
                      pub.setSubscribed(true);
                    } catch (e) {}
                  }
                });
              }
            });
          }
        } catch (e) {
          console.warn("[call-session] unhold on transfer error:", e);
        }
        return;
      }

      // Preserve 'calling' state if this is an outbound call while connecting WebRTC in background
      if (callStateRef.current !== CALL_STATES.calling) {
        setCallState(CALL_STATES.connecting);
      }

      if (liveKitRoomRef.current) {
        await disconnectLiveKitRoom(liveKitRoomRef.current);
        liveKitRoomRef.current = null;
      }
      const room = await connectToLiveKitRoom(callId, "Staff Member", () => {
        clearConnectingTimer();
        setCallState(CALL_STATES.connected);
      });

      // Listen for LiveKit Data messages (HOLD, TRANSFERRING, etc.)
      if (room) {
        room.on("dataReceived", (payload) => {
          try {
            const text = new TextDecoder().decode(payload);
            const message = JSON.parse(text);
            if (message?.type === "HOLD") {
              setIsRemoteHeld(Boolean(message.isHeld));
            } else if (message?.type === "TRANSFERRING" || message?.type === "TRANSFER") {
              setIsTransferring(true);
              setTransferringDepartment(message.targetDepartment || "Department");
            }
          } catch (e) {
            console.warn("[LiveKitWebEngine] dataReceived parse error:", e);
          }
        });
      }

      liveKitRoomRef.current = room;
      clearConnectingTimer();
      setCallState(CALL_STATES.connected);
    } catch (err) {
      failCall(err, "Failed to connect LiveKit audio room.");
    }
  }, [clearConnectingTimer, failCall]);

  const loadIceServers = useCallback(async () => {
    return Promise.resolve();
  }, []);

  const acceptCall = useCallback(async (explicitCallId) => {
    const targetCallId =
      explicitCallId ||
      callInfoRef.current?.callId ||
      callInfoRef.current?.CallId ||
      callInfo?.callId ||
      callInfo?.CallId;

    if (!connection || !targetCallId) {
      debugCall("acceptCall skipped: missing connection or callId", {
        hasConnection: Boolean(connection),
        targetCallId,
        callInfo: callInfoRef.current,
      });
      return;
    }

    try {
      getGlobalCallRingtone().stop();
      setCallState(CALL_STATES.connecting);
      await invokeCallHub(connection, CALL_HUB_METHODS.openIncomingCall, { callId: targetCallId }).catch(() => {});
      await invokeCallHub(connection, CALL_HUB_METHODS.acceptCall, { callId: targetCallId });
      await connectLiveKit(targetCallId);
    } catch (err) {
      failCall(err, "Failed to accept call.");
    }
  }, [callInfo?.CallId, callInfo?.callId, connectLiveKit, connection, failCall]);

  const holdAndAnswerWaiting = useCallback(async () => {
    if (!waitingCall || !connection) return;

    try {
      const activeCallToHold = callInfoRef.current;
      const waitingCallId = waitingCall.callId || waitingCall.CallId;

      // Put active room on hold
      if (liveKitRoomRef.current) {
        await holdLiveKitCall(liveKitRoomRef.current, true).catch(() => {});
        heldLiveKitRoomRef.current = liveKitRoomRef.current;
        liveKitRoomRef.current = null;
      }
      setHeldCall(activeCallToHold);

      // Accept waiting call
      await invokeCallHub(connection, CALL_HUB_METHODS.acceptCallWaiting, {
        callId: waitingCallId,
        holdActiveCall: true,
      });

      // Connect to waiting call's room
      setCallInfo(waitingCall);
      setWaitingCall(null);
      await connectLiveKit(waitingCallId);
    } catch (err) {
      console.error("[useCallSession] holdAndAnswerWaiting error:", err);
    }
  }, [connectLiveKit, connection, setCallInfo, waitingCall]);

  const swapActiveLine = useCallback(async () => {
    if (!heldCall || !callInfoRef.current || !connection) return;

    try {
      const currentActive = callInfoRef.current;
      const currentHeld = heldCall;

      const currentActiveRoom = liveKitRoomRef.current;
      const currentHeldRoom = heldLiveKitRoomRef.current;

      // Swap LiveKit audio holds
      if (currentActiveRoom) {
        await holdLiveKitCall(currentActiveRoom, true).catch(() => {});
      }
      if (currentHeldRoom) {
        await holdLiveKitCall(currentHeldRoom, false).catch(() => {});
      }

      // Swap refs
      liveKitRoomRef.current = currentHeldRoom;
      heldLiveKitRoomRef.current = currentActiveRoom;

      // Swap states
      setCallInfo(currentHeld);
      setHeldCall(currentActive);

      await invokeCallHub(connection, CALL_HUB_METHODS.swapActiveCall, {
        holdCallId: currentActive.callId || currentActive.CallId,
        resumeCallId: currentHeld.callId || currentHeld.CallId,
      }).catch(() => {});
    } catch (err) {
      console.error("[useCallSession] swapActiveLine error:", err);
    }
  }, [connection, heldCall, setCallInfo]);

  const dismissWaitingCall = useCallback(() => {
    setWaitingCall(null);
  }, []);

  const declineCall = useCallback(async (explicitCallId) => {
    const targetCallId =
      explicitCallId ||
      callInfoRef.current?.callId ||
      callInfoRef.current?.CallId ||
      callInfo?.callId ||
      callInfo?.CallId;

    getGlobalCallRingtone().stop();
    resetCall();
    setCallState(CALL_STATES.idle);
    setCallInfo(null);
    onCallEndedRef.current?.();
    if (connection) {
      await invokeCallHub(connection, CALL_HUB_METHODS.declineCall, {
        callId: targetCallId || undefined,
        reason: "declined",
      }).catch(() => {});
    }
  }, [callInfo?.CallId, callInfo?.callId, connection, resetCall, setCallInfo]);

  const endCall = useCallback(async (explicitCallId) => {
    const targetCallId =
      explicitCallId ||
      callInfoRef.current?.callId ||
      callInfoRef.current?.CallId ||
      callInfo?.callId ||
      callInfo?.CallId;

    getGlobalCallRingtone().stop();
    resetCall();
    setCallState(CALL_STATES.idle);
    setCallInfo(null);
    onCallEndedRef.current?.();
    if (connection) {
      await invokeCallHub(connection, CALL_HUB_METHODS.endCall, {
        callId: targetCallId || undefined,
        reason: "ended",
      }).catch(() => {});
    }
  }, [callInfo?.CallId, callInfo?.callId, connection, resetCall, setCallInfo]);

  const holdCall = useCallback(async (nextHeld) => {
    const targetHeld = nextHeld !== undefined ? Boolean(nextHeld) : !isHeld;
    setIsHeld(targetHeld);
    if (liveKitRoomRef.current) {
      try {
        await holdLiveKitCall(liveKitRoomRef.current, targetHeld);
      } catch (e) {
        console.warn("[call-session] holdLiveKitCall error:", e);
      }
    }
  }, [isHeld]);

  const transferCall = useCallback(async (targetDepartmentId, targetDepartmentName = "") => {
    const targetCallId =
      callInfoRef.current?.callId ||
      callInfoRef.current?.CallId ||
      callInfo?.callId ||
      callInfo?.CallId;

    if (!connection || !targetCallId || !targetDepartmentId) {
      throw new Error("Missing active call or target department for transfer.");
    }

    try {
      if (liveKitRoomRef.current) {
        await transferLiveKitCall(liveKitRoomRef.current, targetDepartmentId);
      }

      await invokeCallHub(connection, CALL_HUB_METHODS.transferCall, {
        callId: targetCallId,
        targetDepartment: targetDepartmentId,
        targetDepartmentId: typeof targetDepartmentId === "string" && targetDepartmentId.includes("-") ? targetDepartmentId : null,
        staffName: "Staff Member",
      });

      resetCall();
      setCallState(CALL_STATES.idle);
      setCallInfo(null);
      onCallEndedRef.current?.();
      return true;
    } catch (err) {
      debugCall("transferCall error:", err);
      throw err;
    }
  }, [callInfo?.CallId, callInfo?.callId, connection, resetCall, setCallInfo]);

  const createOffer = useCallback(async (callId) => {
    await connectLiveKit(callId);
  }, [connectLiveKit]);

  const handleSignal = useCallback(async () => {
    // No-op for LiveKit (replaces legacy WebRTC SDP signaling)
  }, []);

  const handleCallEnded = useCallback((payload = {}) => {
    const reason = payload.reason || payload.Reason;
    setLastEndReason(reason || null);
    resetCall();
    setCallState(CALL_STATES.idle);
    setCallInfo(null);
    onCallEndedRef.current?.();
  }, [resetCall, setCallInfo]);

  const toggleMute = useCallback(() => {
    if (liveKitRoomRef.current?.localParticipant) {
      const nextMuted = !muted;
      liveKitRoomRef.current.localParticipant.setMicrophoneEnabled(!nextMuted);
      setMuted(nextMuted);
    }
  }, [muted]);

  return {
    callState,
    callInfo,
    waitingCall,
    heldCall,
    muted,
    isHeld,
    isRemoteHeld,
    isTransferring,
    transferringDepartment,
    callDuration,
    lastError,
    lastEndReason,
    setCallInfo,
    setCallState,
    setIsTransferring,
    setTransferringDepartment,
    setIsHeld,
    setIsRemoteHeld,
    resetCall,
    acceptCall,
    holdAndAnswerWaiting,
    swapActiveLine,
    dismissWaitingCall,
    declineCall,
    endCall,
    holdCall,
    transferCall,
    connectLiveKit,
    createOffer,
    handleSignal,
    handleCallEnded,
    toggleMute,
    loadIceServers,
  };
};
